import { __awaiter } from "tslib";
import React, { useEffect, useState } from 'react';
import { default as request } from "@pb/utils/request";
import { default as getCookie } from "@pb/utils/getCookie";
import { default as consoleEvents } from "@pb/utils/consoleEvents";
import { default as IS_CLIENT } from "@pb/utils/constants/isClient";
import cn from 'classnames';
import { PraIds } from '../../constants';
import { mapRecommendationsToCarousel, normalizeEsalesResponse, refreshSecondCarousel, errorHandler, getSettingsForEsalesQuery, attachColorSwatchListeners, shouldWaitForDataLoad, getStyleWithDefaultsForVariant, callE4Api, } from '../../helpers';
import { getCachedArticles, setArticleToCache } from '../../helpers/cache';
import { matchInArray, optimizelyExperimentPDEMonkiBrandExposedImpact, } from '../../helpers/optimizely/optimizely';
import { CarouselV1 as Carousel } from '../Carousel';
import styles from './ESalesContainerV1.module.scss';
const getInitialArticleIdFromPDP = () => {
    var _a;
    return ((_a = window.productArticleDetails) === null || _a === void 0 ? void 0 : _a.articleCode) || null;
};
const ESalesContainerV1 = ({ className, praConfiguration, articledata, articleid, refreshcarousel = false, externaldata, carouseltitle, imagetype, pralocale, baseproductcode: baseProductCode, stylewitharticles, categoryparentkey, textpriorprice, queryPayload, breakpoint, }) => {
    const { BRAND, ID, API, PANEL_ID, LAZYLOAD_IMAGES } = praConfiguration;
    const [articleId, setArticleId] = useState(getInitialArticleIdFromPDP());
    const [showCarousel, setShowCarousel] = useState(false);
    const [finalProducts, setProducts] = useState([]);
    const [carouselTitle, setCarouselTitle] = useState('');
    const [labels, setLabels] = useState();
    const [confOptimizelyExperimentPDEMonkiBrandExposedImpact, setConfOptimizelyExperimentPDEMonkiBrandExposedImpact,] = useState({
        enabled: false,
        variationKey: 'off',
    });
    const lazyLoadedArticles = (arrayOfArticles) => {
        const numberOfItemsPerSlide = praConfiguration.NUM_OF_ITEMS_PER_SLIDE[breakpoint];
        return arrayOfArticles.map((article, index) => {
            const shouldUseLazy = !(index < numberOfItemsPerSlide);
            return Object.assign(Object.assign({}, article), (shouldUseLazy && {
                shouldLazyLoad: shouldUseLazy,
                shouldDelayLazyLoad: shouldUseLazy,
                lazyLoadDelayMs: 500,
            }));
        });
    };
    const prepareDataForCarouselV2 = (data, isE4FeatureFlagEnabled = false) => {
        const { displayName, articles } = data;
        const mappedArticles = mapRecommendationsToCarousel(Object.assign({ articles, disableFavorites: process.env.IS_PRA_CAROUSEL_FAVOURITES_ENABLED !== 'enabled', praConfiguration, isE4Enabled: isE4FeatureFlagEnabled }, (textpriorprice && { priorPriceLabel: textpriorprice })));
        const hasTitleFromAEM = carouseltitle && carouseltitle.length > 0;
        setProducts(mappedArticles);
        setCarouselTitle((hasTitleFromAEM && carouseltitle) || displayName || '');
        setShowCarousel(true);
    };
    const prepareDataForCarousel = (data, isE4FeatureFlagEnabled = false) => {
        const normalizedData = normalizeEsalesResponse(data, isE4FeatureFlagEnabled);
        try {
            if (!(normalizedData === null || normalizedData === void 0 ? void 0 : normalizedData.length)) {
                throw new Error('Invalid data structure.');
            }
            const panelIds = [];
            const correctPanel = ID === PraIds.SWG || ID === PraIds.PRA1 || ID === PraIds.PRA3 || ID === PraIds.PRA4
                ? normalizedData.find((panel) => {
                    panelIds.push(panel.id);
                    return PANEL_ID === null || PANEL_ID === void 0 ? void 0 : PANEL_ID.includes(panel.id);
                })
                : normalizedData[0];
            if (!correctPanel) {
                throw new Error(`No panel id match found. ${panelIds.join(' ')}`);
            }
            const { products, name } = correctPanel;
            let finalArticles;
            const mappedArticles = mapRecommendationsToCarousel(Object.assign({ articles: products, disableFavorites: process.env.IS_PRA_CAROUSEL_FAVOURITES_ENABLED !== 'enabled', eSalesAsSource: true, praConfiguration, isE4Enabled: isE4FeatureFlagEnabled }, (textpriorprice && { priorPriceLabel: textpriorprice })));
            if (articleId) {
                finalArticles = mappedArticles.filter((article) => article.articleCode !== articleId);
                if (ID === PraIds.SWG && finalArticles.length > 12) {
                    finalArticles = finalArticles.slice(0, 12);
                }
            }
            if (LAZYLOAD_IMAGES && ID === PraIds.SWG && finalArticles) {
                finalArticles = lazyLoadedArticles(finalArticles);
            }
            setProducts(finalArticles || mappedArticles);
            setCarouselTitle(name || '');
            setShowCarousel(true);
        }
        catch (error) {
            errorHandler(error);
            setShowCarousel(false);
        }
        finally {
            if (BRAND === 'hm') {
                refreshSecondCarousel({ praType: ID });
            }
        }
    };
    const fetchRecommendations = (updatedSwgArticles) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        try {
            const isE4FeatureFlagEnabled = false;
            if (externaldata) {
                if (externaldata.panels) {
                    prepareDataForCarousel(externaldata, isE4FeatureFlagEnabled);
                }
                else {
                    throw new Error('No panels available.');
                }
            }
            else {
                const esalesQuerySettings = getSettingsForEsalesQuery({
                    praConfiguration,
                    articledata,
                    articleId: articleId || undefined,
                    pralocale,
                    baseProductCode: updatedSwgArticles && articleId ? articleId : baseProductCode,
                    stylewitharticles: updatedSwgArticles || stylewitharticles,
                    categoryparentkey,
                    queryPayload,
                });
                if (!esalesQuerySettings) {
                    throw new Error('No settings for query.');
                }
                const { path, payload } = esalesQuerySettings;
                try {
                    if (isE4FeatureFlagEnabled) {
                        const e4Data = (yield callE4Api(articleId));
                        setArticleToCache({ praConfiguration, articleId, data: e4Data });
                        prepareDataForCarousel(e4Data, isE4FeatureFlagEnabled);
                    }
                    else {
                        const { data: e3Data } = yield request.post(path, Object.assign({ type: 'pra', data: payload }, (BRAND !== 'hm' &&
                            pralocale && {
                            customLocale: pralocale === 'en' ? (_a = getCookie('HMCORP_locale')) === null || _a === void 0 ? void 0 : _a.toLowerCase() : pralocale,
                        })));
                        if (BRAND === 'hm' && articleId && ((_b = e3Data.panels[0]) === null || _b === void 0 ? void 0 : _b.panels)) {
                            setArticleToCache({ praConfiguration, articleId, data: e3Data });
                        }
                        if (API.VERSION === 2) {
                            prepareDataForCarouselV2(e3Data, isE4FeatureFlagEnabled);
                        }
                        else {
                            prepareDataForCarousel(e3Data, isE4FeatureFlagEnabled);
                        }
                    }
                }
                catch (e) {
                    consoleEvents.error(e);
                }
            }
        }
        catch (error) {
            errorHandler(error);
            setShowCarousel(false);
            if (BRAND === 'hm' && ID === PraIds.SWG) {
                refreshSecondCarousel({ praType: ID });
            }
        }
    });
    // Temporarily added for A/B testing
    // TODO: remove when iterative testing of component is completed. PXREC-2968
    const injectRelatedArticlesPRA = () => {
        try {
            // remove all instances of pra2 before injecting new one to pdp
            document.querySelectorAll('#prod-reco-pra2').forEach((pra2Element) => pra2Element.remove());
            const styleWithPRA = document.querySelector('#product-reco-swg');
            const styleWithContainer = styleWithPRA === null || styleWithPRA === void 0 ? void 0 : styleWithPRA.parentElement;
            const pra2WebComponent = document.createElement('hm-personalized-reco-carousel');
            styleWithContainer === null || styleWithContainer === void 0 ? void 0 : styleWithContainer.insertBefore(pra2WebComponent, styleWithPRA);
            const componentProps = {
                pratype: 'PRA2',
                articleid,
                relatedarticlelabel: JSON.stringify(labels),
            };
            pra2WebComponent.props = componentProps;
            pra2WebComponent.id = 'prod-reco-pra2';
        }
        catch (error) {
            consoleEvents.error('Error adding related article component to PDP page:', error);
        }
    };
    useEffect(() => {
        if (articleid && articleid !== articleId) {
            setArticleId(`${articleid}`);
        }
    }, [articleid]);
    useEffect(() => {
        if (BRAND === 'hm' && ID === PraIds.PRA1 && refreshcarousel && articleId) {
            const cachedArticles = getCachedArticles(praConfiguration, articleId);
            if (cachedArticles) {
                prepareDataForCarousel(cachedArticles, !!(cachedArticles === null || cachedArticles === void 0 ? void 0 : cachedArticles.recommendationLists));
            }
            else {
                fetchRecommendations(null);
            }
        }
    }, [refreshcarousel]);
    useEffect(() => {
        if (BRAND !== 'hm') {
            const listOfArticles = getStyleWithDefaultsForVariant(articleId);
            fetchRecommendations(stylewitharticles !== listOfArticles ? listOfArticles : null);
        }
        if (BRAND === 'hm' && ID === PraIds.SWG && articleId !== null) {
            fetchRecommendations(null);
        }
    }, [articleId]);
    // Temporarily added for A/B testing
    // TODO: remove when iterative testing of component is completed. PXREC-2968
    useEffect(() => {
        if (BRAND === 'hm' && ID === PraIds.SWG && labels && articleId !== null) {
            injectRelatedArticlesPRA();
        }
    }, [labels, articleId]);
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        if (BRAND === 'otherstories' && ID === PraIds.PRA1) {
            attachColorSwatchListeners(setArticleId);
        }
        if ((ID !== PraIds.PRA1 && ID !== PraIds.SWG) ||
            (ID === PraIds.PRA1 && BRAND === 'hm' && !shouldWaitForDataLoad(praConfiguration))) {
            fetchRecommendations(null);
        }
        if (IS_CLIENT && window) {
            const aemLabels = (_c = (_b = (_a = window === null || window === void 0 ? void 0 : window.hm) === null || _a === void 0 ? void 0 : _a.i18n) === null || _b === void 0 ? void 0 : _b.pdp) === null || _c === void 0 ? void 0 : _c.shoptheset;
            setLabels({
                ctaLabel: (_d = aemLabels === null || aemLabels === void 0 ? void 0 : aemLabels.cta) === null || _d === void 0 ? void 0 : _d.pra,
                praTitle: (_e = aemLabels === null || aemLabels === void 0 ? void 0 : aemLabels.pra) === null || _e === void 0 ? void 0 : _e.heading,
            });
        }
    }, []);
    // Another A/B test: BTPDE-213
    useEffect(() => {
        const stringToMatchAndHideInABTest = 'Monki';
        if (finalProducts.length === 0) {
            return;
        }
        const stringMatched = matchInArray(finalProducts, stringToMatchAndHideInABTest, false);
        if (!stringMatched)
            return;
        optimizelyExperimentPDEMonkiBrandExposedImpact().then((response) => {
            if (response && response.enabled) {
                setConfOptimizelyExperimentPDEMonkiBrandExposedImpact(response);
            }
        });
        if (!(confOptimizelyExperimentPDEMonkiBrandExposedImpact === null || confOptimizelyExperimentPDEMonkiBrandExposedImpact === void 0 ? void 0 : confOptimizelyExperimentPDEMonkiBrandExposedImpact.enabled))
            return;
        if (confOptimizelyExperimentPDEMonkiBrandExposedImpact.variationKey === 'monki_brand_hidden') {
            matchInArray(finalProducts, stringToMatchAndHideInABTest, true);
        }
    }, [finalProducts, confOptimizelyExperimentPDEMonkiBrandExposedImpact.enabled]);
    if (!showCarousel) {
        return null;
    }
    return (React.createElement(Carousel, { className: cn(className, styles.hideEmptySpans), finalProducts: finalProducts, praConfiguration: praConfiguration, carouselTitle: carouselTitle, imageType: imagetype, showDotsWithArrows: process.env.IS_PRA_DOTS_WITH_ARROWS === 'enabled', articleId: articleId, breakpoint: breakpoint }));
};
export default ESalesContainerV1;
//# sourceMappingURL=ESalesContainerV1.js.map